import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert
} from "@mui/material";
import { walletApi } from "../services/api";

const AdminWallet = () => {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [walletDetails, setWalletDetails] = useState(null);
  const [updateForm, setUpdateForm] = useState({
    amount: "",
    type: "credit",
    reason: ""
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchWallets();
  }, []);

  const fetchWallets = async () => {
    try {
      setLoading(true);
      const response = await walletApi.getAllWallets();
      setWallets(response.data.wallets || []);
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Failed to fetch wallets");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUpdateDialog = (wallet) => {
    setSelectedWallet(wallet);
    setUpdateForm({ amount: "", type: "credit", reason: "" });
    setError("");
    setSuccess("");
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedWallet(null);
  };

  const handleOpenDetailDialog = async (wallet) => {
    try {
      const response = await walletApi.getWalletByUser(wallet._id);
      setWalletDetails({
        ...(response.data.wallet || {}),
        transactions: response.data.transactions || []
      });
      setOpenDetailDialog(true);
    } catch (err) {
      console.error("Error:", err);
      setError("Failed to fetch wallet details");
    }
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setWalletDetails(null);
  };

  const handleUpdateWallet = async () => {
    try {
      setError("");
      setSuccess("");

      const parsedAmount = parseFloat(updateForm.amount);
      if (!parsedAmount || parsedAmount <= 0 || !updateForm.reason) {
        return setError("Please enter a valid amount (> 0) and reason");
      }

      setUpdating(true);

      const response = await walletApi.updateWallet(
        selectedWallet._id,
        {
          amount: parsedAmount,
          type: updateForm.type,
          reason: updateForm.reason
        }
      );

      if (response.data.success) {
        setSuccess(`Wallet updated successfully! New balance: ₹${response.data.newBalance}`);
        
        // Update local state
        setWallets((prev) =>
          prev.map((w) =>
            w._id === selectedWallet._id
              ? { ...w, walletBalance: response.data.newBalance }
              : w
          )
        );

        setTimeout(() => {
          handleCloseDialog();
        }, 1500);
      }
    } catch (err) {
      console.error("Update error:", err);
      setError(err.response?.data?.message || "Failed to update wallet");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: "1400px", margin: "0 auto" }}>
      {/* PAGE TITLE */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 3,
          fontSize: "1.75rem",
          color: "#C41E3A"
        }}
      >
        💰 Manage User Wallets
      </Typography>

      {/* STATS */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Paper sx={{ p: 2, flex: 1, minWidth: 150 }}>
          <Typography sx={{ fontSize: "0.85rem", color: "#666" }}>
            Total Users
          </Typography>
          <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, color: "#333" }}>
            {wallets.length}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, minWidth: 150 }}>
          <Typography sx={{ fontSize: "0.85rem", color: "#666" }}>
            Total Wallet Balance
          </Typography>
          <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, color: "#4CAF50" }}>
            ₹{wallets.reduce((sum, w) => sum + (w.walletBalance || 0), 0).toFixed(2)}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, minWidth: 150 }}>
          <Typography sx={{ fontSize: "0.85rem", color: "#666" }}>
            Active Wallets
          </Typography>
          <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, color: "#2196F3" }}>
            {wallets.filter((w) => w.walletBalance > 0).length}
          </Typography>
        </Paper>
      </Box>

      {/* TABLE */}
      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 1200 }}>
          {/* TABLE HEAD */}
          <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
            <TableRow>
              <TableCell sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#333" }}>
                User Name
              </TableCell>
              <TableCell sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#333" }}>
                Email
              </TableCell>
              <TableCell sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#333" }}>
                City
              </TableCell>
              <TableCell align="right" sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#333" }}>
                Wallet Balance (₹)
              </TableCell>
              <TableCell align="center" sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#333" }}>
                Referred
              </TableCell>
              <TableCell align="right" sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#333" }}>
                Referral Earnings (₹)
              </TableCell>
              <TableCell sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#333" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          {/* TABLE BODY */}
          <TableBody>
            {wallets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3, fontSize: "0.95rem" }}>
                  No wallets found
                </TableCell>
              </TableRow>
            ) : (
              wallets.map((wallet) => (
                <TableRow
                  key={wallet._id}
                  sx={{
                    "&:hover": { backgroundColor: "#f9f9f9" },
                    borderBottom: "1px solid #eee"
                  }}
                >
                  {/* User Name */}
                  <TableCell sx={{ fontSize: "0.85rem", color: "#333", fontWeight: 500 }}>
                    {wallet.userName || "Unknown User"}
                  </TableCell>

                  {/* Email */}
                  <TableCell sx={{ fontSize: "0.8rem", color: "#666" }}>
                    {wallet.userEmail}
                  </TableCell>

                  {/* City */}
                  <TableCell sx={{ fontSize: "0.8rem", color: "#666" }}>
                    {wallet.userCity}
                  </TableCell>

                  {/* Balance */}
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: "0.95rem",
                      fontWeight: 700,
                      color: wallet.walletBalance > 0 ? "#4CAF50" : "#999"
                    }}
                  >
                    ₹{wallet.walletBalance?.toFixed(2) || "0.00"}
                  </TableCell>

                  {/* Referred Count */}
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: wallet.referralCount > 0 ? "#2196F3" : "#999"
                    }}
                  >
                    {wallet.referralCount || 0}
                  </TableCell>

                  {/* Referral Earnings */}
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: wallet.referralEarnings > 0 ? "#FF6B35" : "#999"
                    }}
                  >
                    ₹{wallet.referralEarnings?.toFixed(2) || "0.00"}
                  </TableCell>

                  {/* Actions */}
                  <TableCell sx={{ fontSize: "0.85rem" }}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpenDetailDialog(wallet)}
                        sx={{
                          fontSize: "0.75rem",
                          py: 0.5,
                          px: 1,
                          textTransform: "none"
                        }}
                      >
                        View
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleOpenUpdateDialog(wallet)}
                        sx={{
                          fontSize: "0.75rem",
                          py: 0.5,
                          px: 1,
                          textTransform: "none",
                          backgroundColor: "#FF6B35",
                          "&:hover": { backgroundColor: "#E55A2B" }
                        }}
                      >
                        Update
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* UPDATE WALLET DIALOG */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#C41E3A" }}>
          Update Wallet - {selectedWallet?.userName}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, fontSize: "0.9rem" }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2, fontSize: "0.9rem" }}>
              {success}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Type */}
            <TextField
              select
              label="Transaction Type"
              value={updateForm.type}
              onChange={(e) =>
                setUpdateForm({ ...updateForm, type: e.target.value })
              }
              fullWidth
              sx={{ fontSize: "0.95rem" }}
            >
              <MenuItem value="credit" sx={{ fontSize: "0.9rem" }}>
                Credit (Add Money)
              </MenuItem>
              <MenuItem value="debit" sx={{ fontSize: "0.9rem" }}>
                Debit (Remove Money)
              </MenuItem>
            </TextField>

            {/* Amount */}
            <TextField
              label="Amount (₹)"
              type="number"
              value={updateForm.amount}
              onChange={(e) =>
                setUpdateForm({ ...updateForm, amount: e.target.value })
              }
              fullWidth
              placeholder="Enter amount"
              inputProps={{ step: "0.01" }}
              sx={{ fontSize: "0.95rem" }}
            />

            {/* Reason */}
            <TextField
              label="Reason"
              value={updateForm.reason}
              onChange={(e) =>
                setUpdateForm({ ...updateForm, reason: e.target.value })
              }
              fullWidth
              multiline
              rows={3}
              placeholder="Enter reason for this transaction"
              sx={{ fontSize: "0.95rem" }}
            />

            <Box sx={{ backgroundColor: "#f9f9f9", p: 1.5, borderRadius: 1 }}>
              <Typography sx={{ fontSize: "0.8rem", color: "#666", mb: 0.5 }}>
                Current Balance:
              </Typography>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#333" }}>
                ₹{selectedWallet?.walletBalance?.toFixed(2) || "0.00"}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{ fontSize: "0.9rem", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateWallet}
            variant="contained"
            disabled={updating}
            sx={{
              fontSize: "0.9rem",
              textTransform: "none",
              backgroundColor: "#FF6B35",
              "&:hover": { backgroundColor: "#E55A2B" }
            }}
          >
            {updating ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* WALLET DETAILS DIALOG */}
      <Dialog
        open={openDetailDialog}
        onClose={handleCloseDetailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#C41E3A" }}>
          Wallet Details
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: "0.8rem", color: "#999", fontWeight: 600 }}>
                NAME
              </Typography>
              <Typography sx={{ fontSize: "0.95rem", color: "#333", mt: 0.5 }}>
                {walletDetails?.userName}
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.8rem", color: "#999", fontWeight: 600 }}>
                EMAIL
              </Typography>
              <Typography sx={{ fontSize: "0.9rem", color: "#333", mt: 0.5 }}>
                {walletDetails?.userEmail}
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.8rem", color: "#999", fontWeight: 600 }}>
                PHONE
              </Typography>
              <Typography sx={{ fontSize: "0.9rem", color: "#333", mt: 0.5 }}>
                {walletDetails?.userPhone || 'N/A'}
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.8rem", color: "#999", fontWeight: 600 }}>
                CITY
              </Typography>
              <Typography sx={{ fontSize: "0.9rem", color: "#333", mt: 0.5 }}>
                {walletDetails?.userCity || 'N/A'}
              </Typography>
            </Box>

            <Box sx={{ backgroundColor: "#f0f7ff", p: 2, borderRadius: 1 }}>
              <Typography sx={{ fontSize: "0.8rem", color: "#0066cc", fontWeight: 600 }}>
                CURRENT BALANCE
              </Typography>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#0066cc", mt: 0.5 }}>
                ₹{walletDetails?.balance?.toFixed(2) || "0.00"}
              </Typography>
            </Box>

            {/* REFERRAL INFORMATION */}
            <Box sx={{ backgroundColor: "#f0fff4", p: 2, borderRadius: 1 }}>
              <Typography sx={{ fontSize: "0.8rem", color: "#059669", fontWeight: 600, mb: 1 }}>
                REFERRAL INFORMATION
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography sx={{ fontSize: "0.85rem", color: "#333" }}>
                  Referral Code:
                </Typography>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#059669" }}>
                  {walletDetails?.referralCode || 'N/A'}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography sx={{ fontSize: "0.85rem", color: "#333" }}>
                  Users Referred:
                </Typography>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#059669" }}>
                  {walletDetails?.referralCount || 0}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: "0.85rem", color: "#333" }}>
                  Referral Earnings:
                </Typography>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#059669" }}>
                  ₹{walletDetails?.referralEarnings?.toFixed(2) || "0.00"}
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.8rem", color: "#999", fontWeight: 600, mb: 1 }}>
                RECENT TRANSACTIONS
              </Typography>
              <Box
                sx={{
                  maxHeight: 300,
                  overflow: "auto",
                  border: "1px solid #eee",
                  borderRadius: 1
                }}
              >
                {walletDetails?.transactions && walletDetails.transactions.length > 0 ? (
                  walletDetails.transactions.map((transaction, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                        "&:last-child": { borderBottom: "none" }
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#333" }}>
                            {transaction.reason}
                          </Typography>
                          <Typography sx={{ fontSize: "0.75rem", color: "#999", mt: 0.25 }}>
                            {new Date(transaction.createdAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: 700,
                            color:
                              transaction.type === "credit"
                                ? "#4CAF50"
                                : "#F44336"
                          }}
                        >
                          {transaction.type === "credit" ? "+" : "-"} ₹
                          {transaction.amount?.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography sx={{ fontSize: "0.85rem", color: "#999" }}>
                      No transactions
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDetailDialog}
            sx={{ fontSize: "0.9rem", textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminWallet;
